import React, { useEffect } from "react";
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import Home from "./pages/Home";
import RegistrationWithAdhar from "./pages/RegistrationWithAdhar";
import RegistrationWithAdharOTPVerify from "./pages/RegistrationWithAdharOTPVerify";
import CaptureImagePage from "./pages/registration/CaptureImagePage";
import PatientDetailsPage from "./pages/registration/PatientDetailsPage";
import BookConsultantPage from "./pages/registration/BookConsultantPage";
import MakePaymentPage from "./pages/registration/MakePaymentPage";
import SessionEnd from "./pages/registration/SessionEnd";
import RegistrationSuccess from "./pages/RegistrationSuccess";
import Login from "./pages/patient/Login";
import VerifyOTP from "./pages/patient/VerifyOTP";
import SelectPatientProfile from "./pages/patient/SelectPatientProfile";
import SelectExternalPatient from "./pages/patient/SelectExternalPatient";
import Dashboard from "./pages/patient/Dashboard";
import LabReports from "./pages/patient/LabReports";
import RadiologyReports from "./pages/patient/RadiologyReports";
import BookConsultation from "./pages/patient/BookConsultation";
import OpdVisitSuccess from "./pages/patient/OpdVisitSuccess";
import OPDCheckIn from "./pages/patient/OPDCheckIn";
import BookAppointment from "./pages/patient/BookAppointment";
import Billing from "./pages/patient/Billing";
import PatientMakePaymentPage from "./pages/patient/PatientMakePaymentPage";
import Faqs from "./pages/Faqs";
import ProtectedRoute, { isAuthBypassEnabled } from "./components/ProtectedRoute";
import OpdSchedule from "./pages/OpdSchedule";
import OnlinePayment from "./pages/OnlinePayment";
import OnlineDeposit from "./pages/OnlineDeposit";
import CounsaltantReceipt from "./components/CounsaltantReceipt";
import PaymentSuccess from './components/PaymentSuccess';
import PaymentFailure from './components/PaymentFailure';
import PaymentCancel from './components/PaymentCancel';
import PaymentCallback from "./components/PaymentCallback";
import ServicePaymentSuccessPage from './components/ServicePaymentSuccessPage';
import AppointmentPaymentSuccess from './components/AppointmentPaymentSuccess';
import DepositPaymentSuccess from './pages/DepositPaymentSuccess';
import DepositPaymentFailure from './pages/DepositPaymentFailure';
import OnlinePaymentLink from "./pages/OnlinePaymentLink";
import PaymentHistory from "./pages/PaymentHistory";
import PaymentStatusChecker from "./pages/PaymentStatusChecker";
import NotFound from "./pages/NotFound";
import axios from "axios";
import { useSelector } from 'react-redux';
import PatientSiteSelector from './components/PatientSiteSelector';
import ReportsAndBills from './pages/patient/ReportsAndBills';
import Feedback from './pages/patient/Feedback';

function App() {
    const kioskToken = useSelector((state) => state.auth.kiosk_token);

    useEffect(() => {
        const getToken = async () => {
            const userName = process.env.REACT_APP_KIOSK_ADMIN_USER;
            // CRA dotenv-expand treats $VAR as env vars — store $ as #DOLLAR# in .env
            const password = (process.env.REACT_APP_KIOSK_ADMIN_PASS || '').replaceAll('#DOLLAR#', '$');

            if (!userName || !password) {
                console.error("Kiosk admin credentials missing: set REACT_APP_KIOSK_ADMIN_USER and REACT_APP_KIOSK_ADMIN_PASS");
                return;
            }

            try {
                const res = await axios.post(
                    `${process.env.REACT_APP_KIOSK_OPS_URL}/validateUserPassword`,
                    null,
                    { params: { userName, password } }
                );
                const newToken = res.data.tokenNo || "";
                const newDepartmentId = res.data.departmentId || "";

                localStorage.setItem("token", newToken);
                localStorage.setItem("departmentId", newDepartmentId);
            } catch (error) {
                console.error("Error fetching HIMS machine token:", error.message || error);
            }
        };

        getToken();
    }, []);

    return (
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            {(kioskToken || isAuthBypassEnabled()) && <PatientSiteSelector />}
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/opd-schedule" element={<ProtectedRoute><OpdSchedule /></ProtectedRoute>} />
                <Route path="/registration-with-adhar" element={<RegistrationWithAdhar />} />
                <Route path="/registration-with-adhar-otp-verify" element={<RegistrationWithAdharOTPVerify />} />
                <Route path="/capture-patient-image" element={<CaptureImagePage />} />
                <Route path="/patient-details" element={<PatientDetailsPage />} />
                <Route path="/registration-success" element={<RegistrationSuccess />} />
                <Route path="/verify-payment" element={<PaymentCallback />} />

                <Route path="/online-payment" element={<OnlinePayment />} />
                <Route path="/online-deposit" element={<ProtectedRoute><OnlineDeposit /></ProtectedRoute>} />
                <Route path="/service-payment-success" element={<ServicePaymentSuccessPage />} />
                <Route path="/deposit-payment-success" element={<DepositPaymentSuccess />} />
                <Route path="/deposit-payment-failure" element={<DepositPaymentFailure />} />
                <Route path="/pay" element={<OnlinePaymentLink />} />
                <Route path="/payment-history" element={<PaymentHistory />} />
                <Route path="/payment-history/:mrno" element={<PaymentHistory />} />
                <Route path="/payment-status-checker" element={<PaymentStatusChecker />} />

                <Route path="/book-consultant" element={<BookConsultantPage />} />
                <Route path="/make-payment" element={<MakePaymentPage />} />
                <Route path="/session-end" element={<SessionEnd />} />
                <Route path="/consultant-receipt" element={<CounsaltantReceipt />} />

                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/payment-failure" element={<PaymentFailure />} />
                <Route path="/payment-cancel" element={<PaymentCancel />} />
                <Route path="/appointment-success" element={<AppointmentPaymentSuccess />} />

                <Route path="/login" element={<Login />} />
                <Route path="/verify-otp" element={<VerifyOTP />} />

                <Route path="/select-patient-profile" element={<SelectPatientProfile />} />
                <Route path="/select-external-patient" element={<SelectExternalPatient />} />
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/opd-check-in" element={<ProtectedRoute><OPDCheckIn /></ProtectedRoute>} />
                <Route path="/book-appointment" element={<ProtectedRoute><BookAppointment /></ProtectedRoute>} />
                <Route path="/patient-make-payment-page" element={<ProtectedRoute><PatientMakePaymentPage /></ProtectedRoute>} />
                <Route path="/book-consultation" element={<ProtectedRoute><BookConsultation /></ProtectedRoute>} />
                <Route path="/opd-visit-success" element={<ProtectedRoute><OpdVisitSuccess /></ProtectedRoute>} />
                <Route path="/lab-reports" element={<ProtectedRoute><LabReports /></ProtectedRoute>} />
                <Route path="/radiology-reports" element={<ProtectedRoute><RadiologyReports /></ProtectedRoute>} />
                <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
                <Route path="/reports-bills" element={<ProtectedRoute><ReportsAndBills /></ProtectedRoute>} />
                <Route path="/feedback" element={<ProtectedRoute><Feedback /></ProtectedRoute>} />

                <Route path="/faqs" element={<ProtectedRoute><Faqs /></ProtectedRoute>} />
                <Route path="*" element={<NotFound />} />
            </Routes>
        </Router>
    );
}

export default App;
